import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getCatalog } from "./statutory/statutoryReportsApi";
import {
  REPORTS_CATEGORY_ORDER,
  buildReportsByCategory,
  resolveActiveNavItem,
} from "./reportsNavConfig";

const FAV_KEY = "prw:favorites";
const RECENT_KEY = "prw:recent";

function readJson(key, fallback) {
  try {
    return JSON.parse(localStorage.getItem(key)) ?? fallback;
  } catch {
    return fallback;
  }
}

/**
 * Shared reports navigation state for ReportsEnterpriseShell across all report workspaces.
 */
export function useReportsNavigation({ payrollReports = [] } = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [catalog, setCatalog] = useState([]);
  const [navSearch, setNavSearch] = useState("");
  const [favorites, setFavorites] = useState(() => readJson(FAV_KEY, []));
  const [recent, setRecent] = useState(() => readJson(RECENT_KEY, []));

  useEffect(() => {
    getCatalog()
      .then((res) => setCatalog(res.reports || []))
      .catch(() => setCatalog([]));
  }, []);

  const reportsByCategory = useMemo(
    () => buildReportsByCategory({ payrollReports, catalog, search: navSearch }),
    [payrollReports, catalog, navSearch]
  );

  const activeItem = useMemo(
    () =>
      resolveActiveNavItem({
        pathname: location.pathname,
        search: location.search,
        payrollReports,
        catalog,
      }),
    [location.pathname, location.search, payrollReports, catalog]
  );

  const categoryOrder = useMemo(() => {
    const withItems = REPORTS_CATEGORY_ORDER.filter(
      (cat) => (reportsByCategory[cat] || []).length > 0
    );
    return withItems.length ? withItems : REPORTS_CATEGORY_ORDER;
  }, [reportsByCategory]);

  const allNavItems = useMemo(
    () => Object.values(reportsByCategory).flat(),
    [reportsByCategory]
  );

  const favReports = useMemo(
    () => favorites.map((k) => allNavItems.find((r) => r.key === k)).filter(Boolean),
    [favorites, allNavItems]
  );

  const recentReports = useMemo(
    () => recent.map((k) => allNavItems.find((r) => r.key === k)).filter(Boolean),
    [recent, allNavItems]
  );

  const pushRecent = useCallback((key) => {
    if (!key) return;
    setRecent((prev) => {
      const next = [key, ...prev.filter((k) => k !== key)].slice(0, 8);
      localStorage.setItem(RECENT_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const onSelectReport = useCallback(
    (report) => {
      if (!report) return;
      setNavSearch("");
      if (report.key) pushRecent(report.key);
      if (report.href) {
        navigate(report.href);
        return;
      }
      navigate(`/reports/payroll?report=${encodeURIComponent(report.key)}`);
    },
    [navigate, pushRecent]
  );

  const toggleFavorite = useCallback((key, e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    setFavorites((f) => {
      const next = f.includes(key) ? f.filter((k) => k !== key) : [...f, key];
      localStorage.setItem(FAV_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const moduleTitle = useMemo(() => {
    if (location.pathname === "/reports/esi") return "ESI Reports";
    if (location.pathname === "/reports/statutory") return "Statutory Reports";
    if (location.pathname === "/reports/payroll/employee-salary-register") return "Salary Register";
    if (location.pathname === "/bank/reports") return "Banking Reports";
    if (location.pathname === "/reports/leave") return "Leave Reports";
    return "Reports";
  }, [location.pathname]);

  return {
    categoryOrder,
    reportsByCategory,
    selectedKey: activeItem?.key || "",
    selectedReport: activeItem,
    reportSearch: navSearch,
    onReportSearchChange: setNavSearch,
    favorites,
    onToggleFavorite: toggleFavorite,
    recentReports,
    favReports,
    onSelectReport,
    moduleTitle,
    catalog,
    pushRecent,
  };
}
